import { Component, OnInit, Input } from '@angular/core';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PlansStateStructure } from '../configs/interfaces/plansStateStructure.interface';
import { DRX_ENABLED_PRODUCT_GROUPS, SENIOR_PRODUCT_GROUPS } from '../enums/product-groups.enum';
import { Product } from '../interfaces/product.interface';
import { SummaryView, PlanAttributes } from '../interfaces/plan-attributes.interface';
import { PlanDetailsService } from '../plan-details/plan-details.service';
import { PlanDetailsRenderableCategory, PlanDetailsRenderableGroup } from '../plan-details/types/plan-details.types';
import { BooleanInput, ContentStorageService } from '@ptl/global-components';
import { Pharmacy } from '../../medical-information/pharmacy-information/configs/interfaces/pharmacy.interface';
import { select } from '@ptl/angular-redux-store';
import { Observable, Subject } from 'rxjs';
import { DrxService } from '../../medical-information/medication-information/services/drx.service';
import { PharmacyState, PHARMACY_REDUX_STATE_ID } from '../../medical-information/pharmacy-information/configs/reducers/pharmacy.reducer';
import { environment } from 'src/environments/environment';
import { DRUG_TOGGLE_OPTIONS } from '../../../shared/enums/drug-costs.enum';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MedicationStateStructure } from '../../medical-information/medication-information/reducers/medication.reducer';
import { DRXRetrieveAllProductsItem } from '../../../shared/interfaces/drx.interface';
import { DRXStateStructure } from '../../medical-information/medication-information/reducers/drx-response.reducer';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { updateActiveTab } from '../configs/reducers/plan-details-active-tab.reducer';
@Component({
    selector: 'app-plan-and-drx-tabs',
    templateUrl: './plan-and-drx-tabs.component.html',
    styleUrls: ['./plan-and-drx-tabs.component.scss'],
    standalone: false
})
export class PlanAndDrxTabsComponent implements OnInit {

  DETAILS_TAB = 'DETAILS';
  DRX_TAB = 'DRX';
  PREMIUM_TAB = 'PREMIUM';
  DOCUMENTS_TAB = 'DOCUMENTS';
  
  @Input() enrollmentFlow: string;
  @Input() quoteButtonPage: string;
  
  /** URLs */
  @Input() enrollStartUrl: string;
  @Input() specialEnrollmentUrl: string;
  @Input() wholeHealthBalanceEnrollStartUrl: string;
  @Input() viewPlansUrl: string;
  @Input() recommendedPlansUrl: string;
  @Input() sendReferralUrl: string; 
  @Input() @BooleanInput() showSaveQuoteButton: boolean; // not a url, but also lives in plan-details-header
  @Input() @BooleanInput() enableNewPrintExperience: boolean; // not a url, but also lives in plan-details-header
  @Input() saveQuoteText: string;
  @Input() returnUrl: string;

  /** Mat-Tab-Group Labels */
  @Input() planDetailsTabLabel: string;
  @Input() totalDrugCostsTabLabel: string;
  @Input() totalCostsTabLabel: string;

  @Input() yearlyDrugCostsHeading: string;
  @Input() coveragePhaseHeading: string;
  @Input() monthlyTableHeading: string;
  @Input() monthlyCostDescriptionContent: string;
  @Input() yearlyCostDescriptionContent: string;

  @Input() addMedicationsUrl: string;
  @Input() addMedicationRedirectLabel: string;
  @Input() market: string;
  @Input() content: string;
  @Input() showPlanDocs: string = "false";
  @Input() showPremiumDetails: string = "false";
  @Input() showDoctorDetails: string = "false";

  @Input() enableHighlightBenefitDifferences: string;
  tabChangeSubject: Subject<void> = new Subject<void>();

  plans: PlanDetailsRenderableCategory[];
  plansFullData: Product<PlanAttributes, SummaryView>[];
  iconographyDetails: PlanDetailsRenderableGroup;
  environment = environment;

  @select(['componentStates', 'DrxServiceData', 'drxToggleOption'])
  retailMailOrderSwitch$: Observable<DRUG_TOGGLE_OPTIONS>;

  @select(['componentStates', 'pharmacys', 'selectedPharmacy'])
  pharmacy$: Observable<Pharmacy>;
  selectedPharmacy: Pharmacy;
  isLoadingDrx: boolean = false;
  isLoadingPharmacy: boolean = false;

  showTabs = false;
  enableDrx = false;
  hasDrx = false;
  hasPlanDocs = false;
  hasPremiumDetails = false;



  constructor(
    private drxService: DrxService,
    private reducerUtilityService: ReducerUtilityService,
    private contentStorage: ContentStorageService,
    private planDetailsService: PlanDetailsService,
    private ngRedux: NgRedux<StateStructure>
  ) {}

  async ngOnInit() {
    this.configureSubscriptions();
    this.configureTabFlags();
    this.loadPlans();
  }

  goToUrl(url: string) {
    if (url) {
      window.location.href = `./${url}`;
    }
  }

  async changeTabs($event: MatTabChangeEvent) {
    // drug tabs need to make sure the session details are up to date

    this.ngRedux.dispatch(updateActiveTab($event.tab.textLabel));

    if ($event.tab.textLabel === this.DRX_TAB) {
      this.isLoadingDrx = true;
      await this.drxService.updateSession();
      await this.drxService.updatePlanDetails();
      this.isLoadingDrx = false;
    }
    
    // Plan docs has a weird bug and needs to refresh itself when tabs change
    if ($event.tab.textLabel === this.DOCUMENTS_TAB) {
      this.tabChangeSubject.next();
    }
  }

  configureSubscriptions() {
    const pharmacyState: PharmacyState = this.reducerUtilityService.getState(PHARMACY_REDUX_STATE_ID);
    this.selectedPharmacy = pharmacyState.selectedPharmacy;
    this.pharmacy$.subscribe(async (pharmacy: Pharmacy) => {
      if (this.selectedPharmacy && this.selectedPharmacy.pharmacyId !== pharmacy.pharmacyId){
        this.selectedPharmacy = pharmacy;
        this.isLoadingPharmacy = true;

        // Fake a quick load just so the user has some feedback something changed
        this.drxService.generateAndSaveRetailAndMailData();
        await new Promise(x => setTimeout(x, 500));

        this.isLoadingPharmacy = false;
      }
    });

    // Fake a quick load just so the user has some feedback something changed
    this.retailMailOrderSwitch$.subscribe(async (val) => {
      this.isLoadingPharmacy = true;
      await new Promise(x => setTimeout(x, 500));
      this.isLoadingPharmacy = false;
    });
  }

  configureTabFlags() {
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

    this.hasPlanDocs = this.showPlanDocs === 'true';
    this.hasPremiumDetails = this.showPremiumDetails === 'true';
    this.showTabs = SENIOR_PRODUCT_GROUPS.indexOf(planState.activeProductGroup) !== -1 || this.hasPremiumDetails || this.hasPlanDocs;
    this.enableDrx = DRX_ENABLED_PRODUCT_GROUPS.indexOf(planState.activeProductGroup) !== -1;
    
    // show special message if we are in a drx enabled product group but do not currently have any drugs added
    const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');
    if (medState.currentEncodedMeds) {
      this.hasDrx = true;
    }
  }

  async loadPlans() {
    try {
      const jsonContent = JSON.parse(this.content);
      const parsedContent: Map<any, any> = new Map(jsonContent.map(obj => [obj.contentKey, obj.contentValue]));
      this.contentStorage.storeContentMapFromComponent('planDetailsContent', parsedContent);
    }
    catch (err) {
      console.log('JSON Content Parse and Map E', err);
    }

    const responses = await this.planDetailsService.getPlanDetails(this.market);
    if (responses) {
      this.plansFullData = responses.rawData;
      this.plans = responses.transformedData;

      // Only show tabs if we have one plan. More than 1 plan means they are comparing plans so we hide other details
      this.showTabs = this.showTabs && this.plansFullData.length === 1;

      // Hide drug tabs if this plan has no drug coverage
      this.disableDrxTabsIfNoDrugCoverage(this.plansFullData[0]);
    }
  }

  disableDrxTabsIfNoDrugCoverage(plan: Product<PlanAttributes, SummaryView>) {
    const drxData: DRXStateStructure = this.reducerUtilityService.getState('DrxServiceData');

    // Make sure we have drug data
    if (drxData.drxAllProductsResponse) {
      const matchedDrxPlan: DRXRetrieveAllProductsItem[] = drxData.drxAllProductsResponse.filter(drxPlan => {
        return drxPlan.PlanID === plan.attributes.PBP_ID && drxPlan.ContractID === plan.attributes.CMS_PLAN_ID;
      });

      // Make sure this plan has drug coverage
      if (!matchedDrxPlan?.length || !matchedDrxPlan[0].HasDrugCoverage) {
        this.enableDrx = false;
      }
    }
  }
}
